import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin } from 'antd';
import { CheckCircle, Warning, Circle, Sun, BatteryCharging, HouseLine, List } from '@phosphor-icons/react';
import { useOneButtonStore, COMMON_CONSTANTS, type PartnerSyncChecklist } from '../../../../store/oneButtonStore';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import './oneButton-css/FinancePartnerCreateLeadProgressModal.css';
import { OBD_FINANCE_PARTNER_PROGRESS_Z } from './oneButtonOverlayZ';
import { SyncStep, ProductSyncState, FinancePartnerCreateLeadProgressModalProps } from '../../../../@types/oneButtonType';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import {
    invalidateRetrievedOpportunityDetails,
    mergeResetSyncPayloadPreservingOtherProducts,
    isExistingLeadResyncPayload,
    withFinancePartnerLeadList,
} from '../../../../helpers/lightReachLeadId';
import DashboardModal from './DashboardModal';
import {
    getContractApiErrorMessage,
    getContractApiResultErrorMessage,
} from '../../../../helpers/contractApiErrorMessage';
import { usePartnerApiErrorStore } from '../../../../store/partnerApiErrorStore';
import {
    isFinancePartnerSyncFlagDone,
    partnerNeedsUtilityAndLeadSystem,
    syncLeadSystemWithRetries,
    syncUtilityWithRetries,
} from './utils/finance';
import {
    checklistFromFinancePartnerSyncRow,
    findSyncStatusRowForProduct,
    partnerSkipsTitleCheck,
} from './partnerSyncConfig';

const INITIAL_STEPS = (partnerName: string, action: string = 'create', resyncStepId?: string): SyncStep[] => {
    const steps: SyncStep[] = [
        // These are the steps we follow to sync data with the finance partner.
        { id: 'lead', label: action === 'sync' ? 'Sync Lead' : 'Create Lead', status: 'pending' },
        { id: 'contact', label: 'Create Contact', status: 'pending' },
        { id: 'title', label: 'Check Title', status: 'pending' }
    ];

    // Only add Utility and Lead System Sync if the partner is IGS OR LIGHTREACH AND we are in the automation/resync flow.
    // The "create" and "sync" flows should only handle Lead/Contact/Title as per the old flow.
    if (partnerNeedsUtilityAndLeadSystem(partnerName) && action === 'automation_sync') {
        steps.push({ id: 'utility', label: 'Update Utilities', status: 'pending' });
        
        // As requested: "if i click Utility sync or resync no need to show Lead System sync section"
        if (resyncStepId !== 'utility') {
            steps.push({ id: 'leadSystem', label: 'Create Lead System', status: 'pending' });
        }
    }

    // Filter steps to show only up to the requested resync step
    if (resyncStepId === 'titleCheck') {
        const titleIdx = steps.findIndex(s => s.id === 'title');
        if (titleIdx !== -1) return steps.slice(0, titleIdx + 1);
    }
    if (resyncStepId === 'contact') {
        const contactIdx = steps.findIndex(s => s.id === 'contact');
        if (contactIdx !== -1) return steps.slice(0, contactIdx + 1);
    }

    return steps;
};

export const FinancePartnerCreateLeadProgressModal: React.FC<FinancePartnerCreateLeadProgressModalProps & { resyncStepId?: string }> = ({
    open,
    onClose,
    opportunityId,
    partnerName,
    products,
    action = 'create',
    syncPayload,
    utilityRestoreProductTypes = [],
    resyncStepId
}) => {
    const theme = useOneButtonStore((s) => s.theme); // Get the current theme (light or dark) from the store.
    const [productStates, setProductStates] = useState<ProductSyncState[]>([]); // Keep track of the progress for each product like Solar or Battery.
    const [isComplete, setIsComplete] = useState(false); // This tells us when all the steps are finished successfully.
    const [overallError, setOverallError] = useState<string | null>(null); // Store any error message if something goes wrong during the sync.

    const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

    /** Prevent useEffect from re-firing runSyncSequence when parent/store updates change `products` reference. */
    const syncSequenceStartedRef = useRef(false);
    const runSyncSequenceRef = useRef<() => Promise<void>>(async () => {});
    const utilityRestoreRef = useRef<string[]>([]);
    const productsSnapshotRef = useRef(products);

    const handleCloseWrapper = () => {
        // Instantly trigger background fetch
        (async () => {
            try {
                const state = useOneButtonStore.getState();
                const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || opportunityId;
                if (salesforceId) {
                    invalidateRetrievedOpportunityDetails(opportunityId);
                    const retrieved = opportunityId
                        ? await opportunityInfoServices.retrieveOpportunityDetails(String(opportunityId)).catch(() => null)
                        : null;
                    const oppDetail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
                    const base = oppDetail ?? state.opportunityDetails;
                    const merged = withFinancePartnerLeadList(base, retrieved);
                    if (merged) state.setOpportunityDetails(merged);
                    else if (oppDetail) state.setOpportunityDetails(oppDetail);
                }
            } catch (err) {
                console.error('Failed to auto-refresh opp details on close:', err);
            }
        })();
        
        // Unmount instantly
        if (onClose) onClose();
    };

    // Helper to map long product names/types to the short codes used by the finance partner APIs.
    const getNormalizationCode = (type: string) => {
        const t = (type || '').toLowerCase().replace(/\s+only$/, '');
        if (t.includes(BatteryProductName.toLowerCase())) return 'B';
        if (t.includes(RoofProductName.toLowerCase())) return 'R';
        if (t.includes(RRRProductName.toLowerCase())) return 'RRR';
        if (t.includes(SolarProductName.toLowerCase())) return 'S';
        return 'S';
    };

    const updateProductStepStatus = (productIndex: number, stepId: string, status: SyncStep['status'], message?: string) => {
        setProductStates(prev => {
            const next = [...prev];
            const currentProduct = next[productIndex];
            if (!currentProduct) return prev;

            next[productIndex] = {
                ...currentProduct,
                steps: currentProduct.steps.map(step => 
                    step.id === stepId ? { ...step, status, message } : step
                )
            };
            return next;
        });
    };

    // This is the main function that runs all the sync steps one by one.
    const runSyncSequence = useCallback(async () => {
        try {
            const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

            // Helper to check if an API response is successful (handles multiple formats)
            const isResponseSuccess = (res: any) => {
                if (res === true || res === "true") return true;
                if (!res || typeof res !== 'object') return false;
                return res.IsSuccessful === true || 
                       res.isSuccesful === true || 
                       res.isSuccessful === true || 
                       res.IsSuccessful === "true" ||
                       res.isSuccesfull === true ||
                       res.isSuccessfull === true ||
                       res.Message?.toLowerCase().includes('success');
            };

            // Step 1: ResetSyncData for new leads; skip when re-linking the same Lead ID (V1 reSyncLead parity).
            if (action === 'create' || action === 'sync') {
                const opportunityDetails = useOneButtonStore.getState().opportunityDetails;
                const resetPayload = mergeResetSyncPayloadPreservingOtherProducts(
                    opportunityDetails,
                    syncPayload || [],
                );
                const skipResetForExistingLead =
                    action === 'sync' && isExistingLeadResyncPayload(opportunityDetails, resetPayload);
                if (!skipResetForExistingLead) {
                    await quoteServices.resetSyncData(opportunityId, resetPayload);
                }
            }

            // Step 2: Access current sync status from store ONLY if we are in the automation/checklist resync flow.
            let syncList = useOneButtonStore.getState().financePartnerRawSyncStatuses || [];
            if (action === 'automation_sync') {
                // We use the already kept value from oneButton store as requested.
                // If it's empty, we might do a fallback fetch if necessary, but according to requirement we just use the store.
                if (!Array.isArray(syncList) || syncList.length === 0) {
                    const res = await quoteServices.getSyncStatusList(opportunityId);
                    syncList = res?.FinancePartnerSyncStatuses || (Array.isArray(res) ? res : []);
                    useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
                }
            }
            
            const isStepDoneResult = (stepId: string, currentProductType: string) => {
                if (action !== 'automation_sync') return false;
                if (!Array.isArray(syncList) || syncList.length === 0) return false;

                const match = findSyncStatusRowForProduct(syncList, currentProductType);
                if (!match) return false;

                if (stepId === 'credit') return match.IsCreditPass === true;
                const checklist = checklistFromFinancePartnerSyncRow(match);
                switch (stepId) {
                    case 'lead':
                        return checklist.lead === true;
                    case 'contact':
                        return checklist.contact === true;
                    case 'utility':
                        return checklist.utility === true;
                    case 'leadSystem':
                        return checklist.leadSystem === true;
                    case 'title':
                        return checklist.titleCheck === true;
                    default:
                        return isFinancePartnerSyncFlagDone(
                            syncList,
                            currentProductType,
                            stepId as 'lead' | 'contact' | 'utility' | 'leadSystem' | 'title',
                        );
                }
            };

            const isStepDone = (stepId: string, currentProductType: string) => {
                if (action !== 'automation_sync') return false;

                // If this is the specific step we want to resync, pretend it's not done.
                // Map checklist IDs to progress IDs where they differ
                if (resyncStepId && (
                    resyncStepId === stepId || 
                    (resyncStepId === 'titleCheck' && stepId === 'title')
                )) {
                    return false;
                }
                return isStepDoneResult(stepId, currentProductType);
            };

            const activeProducts =
                productsSnapshotRef.current.length > 0 ? productsSnapshotRef.current : products;

            // Loop through each product to sync leads, contacts, and titles.
            for (let i = 0; i < activeProducts.length; i++) {
                const { productType } = activeProducts[i];
                let utilityMarkedDone = isStepDoneResult('utility', productType);
                let leadSystemMarkedDone = isStepDoneResult('leadSystem', productType);

                // Step 2: Make sure a lead exists; if not, create a new one.
                const isLeadDone = isStepDone('lead', productType);
                if (isLeadDone) {
                    updateProductStepStatus(i, 'lead', 'success');
                } else {
                    updateProductStepStatus(i, 'lead', 'syncing');
                    
                    // In the old flow, even for sync, we just verify the lead exists or is refreshed.
                    const productCode = getNormalizationCode(productType);
                    const alreadyBound = (syncPayload || []).some((row: any) => {
                        const keptCategory = String(row.product_Category || '').trim().toUpperCase();
                        if (keptCategory && keptCategory === productCode) return true;
                        return getNormalizationCode(String(row.productType || '')) === productCode;
                    });

                    if (action === 'create' && !alreadyBound) {
                        const createRes = await quoteServices.createLead({
                            opportunityId,
                            productSelection: { Product_Category: productCode, LeadForProduct: productCode }
                        });
                        if (createRes === "" || createRes?.IsSuccessful === false) {
                            throw new Error(
                                getContractApiResultErrorMessage(
                                    createRes,
                                    `Lead creation failed for ${productType}`,
                                ),
                            );
                        }
                    }
                    updateProductStepStatus(i, 'lead', 'success');
                }

                // Step 3: Sync the contact details with the finance partner.
                // As requested: ensure bottom steps sync api is not trigering
                if (resyncStepId === 'lead') continue;

                const isContactDone = isStepDone('contact', productType);
                if (isContactDone) {
                    updateProductStepStatus(i, 'contact', 'success');
                } else {
                    updateProductStepStatus(i, 'contact', 'syncing');
                    await quoteServices.isContactCreated(opportunityId, productType);
                    await quoteServices.createContactFinancePartner(opportunityId, productType, true, action === 'sync');
                    updateProductStepStatus(i, 'contact', 'success');
                }

                // Step 4: Check if the title is verified by the partner.
                // As requested: ensure bottom steps sync api is not trigering
                if (resyncStepId === 'contact' || resyncStepId === 'lead') continue;

                const isTitleDone = isStepDone('title', productType);
                const productPartner = (activeProducts[i] as { financePartner?: string }).financePartner || partnerName;
                // V1 sync-sunnova-model skips FinancePartnerTitleCheck when Lightreach is the partner.
                const skipLightreachTitle = partnerSkipsTitleCheck(productPartner);
                if (isTitleDone || skipLightreachTitle) {
                    updateProductStepStatus(i, 'title', 'success');
                } else {
                    updateProductStepStatus(i, 'title', 'syncing');
                    try {
                        const titleRes = await quoteServices.financePartnerTitleCheck(opportunityId, productType);
                        if (!isResponseSuccess(titleRes)) {
                            updateProductStepStatus(i, 'title', 'error', titleRes?.Message || `Title verification failed for ${productType}`);
                        } else {
                            updateProductStepStatus(i, 'title', 'success');
                        }
                    } catch (err: any) {
                        updateProductStepStatus(i, 'title', 'error', err?.message || 'Title check connection error');
                    }
                }

                // Step 5: Sync utility data with the finance partner if it is IGS or LIGHTREACH.
                if (partnerNeedsUtilityAndLeadSystem(partnerName) && action === 'automation_sync') {
                    // As requested: "after clicking Title check all the remaining section are showing only need to shoup up to Check Tiltle"
                    if (resyncStepId === 'titleCheck' || resyncStepId === 'contact' || resyncStepId === 'lead') continue;

                    const isUtilDone = isStepDone('utility', productType);
                    if (isUtilDone) {
                        updateProductStepStatus(i, 'utility', 'success');
                    } else {
                        updateProductStepStatus(i, 'utility', 'syncing');
                        await syncUtilityWithRetries({
                            opportunityId,
                            productType,
                            partnerName,
                        });
                        utilityMarkedDone = true;
                        updateProductStepStatus(i, 'utility', 'success');
                    }
                }

                // Step 6: Create the Lead System record (the final handshake).
                if (partnerNeedsUtilityAndLeadSystem(partnerName) && action === 'automation_sync') {
                    // As requested: "if i click Utility sync or resync no need to show Lead System sync section and don't triger it's sync api"
                    if (resyncStepId === 'utility') continue;

                    const isLeadSystemDone = isStepDone('leadSystem', productType);
                    if (isLeadSystemDone) {
                        updateProductStepStatus(i, 'leadSystem', 'success');
                    } else {
                        updateProductStepStatus(i, 'leadSystem', 'syncing');
                        const productCode = getNormalizationCode(productType);
                        await syncLeadSystemWithRetries({
                            opportunityId,
                            productType,
                            productCategoryOverride: productCode,
                            onSuccess: async () => {
                                const oppDetails = useOneButtonStore.getState().opportunityDetails;
                                const isMultiContract = oppDetails?.IsMultiContract;
                                const isRoofOnly = oppDetails?.OpportunityInterest?.RoofOnly;
                                if (isMultiContract && isRoofOnly) {
                                    await quoteServices.checkIgnoreTitle(opportunityId);
                                    await quoteServices.creditRollover(opportunityId);
                                }
                            },
                        });
                        leadSystemMarkedDone = true;
                        updateProductStepStatus(i, 'leadSystem', 'success');
                    }
                }

                const newSyncStatus: Partial<PartnerSyncChecklist> = {
                    leadSyncAction: action === 'sync' ? 'sync_existing' : 'create',
                    lead: true,
                    contact: true,
                    titleCheck: true,
                };
                if (partnerNeedsUtilityAndLeadSystem(partnerName) && action === 'automation_sync') {
                    if (utilityMarkedDone) newSyncStatus.utility = true;
                    if (leadSystemMarkedDone) newSyncStatus.leadSystem = true;
                }

                useOneButtonStore.getState().setFinancePartnerSyncStatus(partnerName, newSyncStatus, productType);
            }

            // Defer finance-selection refresh until close — firing here re-rendered parents and re-ran this sequence.
            if (partnerNeedsUtilityAndLeadSystem(partnerName) && action !== 'sync') {
                useOneButtonStore.getState().setFinanceSelectionRefreshTrigger(Date.now());
            }

            // Lightreach FinancePartnerLeadSearch sets IsUtilitiesSync=false; restore when it was synced before.
            const restoreUtilityTypes = utilityRestoreRef.current;
            if (
                action === 'sync' &&
                restoreUtilityTypes.length > 0 &&
                partnerNeedsUtilityAndLeadSystem(partnerName)
            ) {
                for (const productType of restoreUtilityTypes) {
                    const info = productsSnapshotRef.current.find(
                        (p) =>
                            String(p.productType || '')
                                .trim()
                                .toLowerCase() === productType.toLowerCase(),
                    );
                    const pt = info?.productType ?? productType;
                    try {
                        await syncUtilityWithRetries({
                            opportunityId,
                            productType: pt,
                            partnerName,
                        });
                        useOneButtonStore.getState().setFinancePartnerSyncStatus(
                            partnerName,
                            { utility: true },
                            pt,
                        );
                    } catch (restoreErr) {
                        console.error('Utility restore after lead resync failed:', restoreErr);
                    }
                }
                const stats = await quoteServices.getSyncStatusList(opportunityId);
                const list = stats?.FinancePartnerSyncStatuses || (Array.isArray(stats) ? stats : []);
                if (Array.isArray(list)) {
                    useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(list);
                }
            }

            setIsComplete(true);
        } catch (error: unknown) {
            let errorText = getContractApiErrorMessage(error, 'Workflow interrupted');
            if (/^(false|true|null|undefined)$/i.test(errorText.trim())) {
                errorText = 'Workflow interrupted';
            }
            setOverallError(errorText);
            setProductStates(prev => prev.map(ps => ({
                ...ps,
                steps: ps.steps.map(s => s.status === 'syncing' ? { ...s, status: 'error', message: errorText } : s)
            })));
        }
    }, [opportunityId, products, action, syncPayload, partnerName]);

    runSyncSequenceRef.current = runSyncSequence;

    // Start the sync process once per open (avoid UpdateUtilities / GetSyncStatusList loops).
    useEffect(() => {
        if (!open) {
            syncSequenceStartedRef.current = false;
            return;
        }
        if (syncSequenceStartedRef.current) return;
        syncSequenceStartedRef.current = true;

        utilityRestoreRef.current = [...utilityRestoreProductTypes];
        productsSnapshotRef.current = products;

        const releaseSuppress = usePartnerApiErrorStore.getState().pushSuppressPartnerErrorPopup();
        setProductStates(
            products.map((p) => ({
                info: p,
                steps: INITIAL_STEPS(partnerName, action, resyncStepId ?? undefined),
            })),
        );
        setIsComplete(false);
        setOverallError(null);
        void runSyncSequenceRef.current();
        return releaseSuppress;
    }, [open, partnerName, action, resyncStepId, utilityRestoreProductTypes, products]);

    // Pick the right icon (spinner, checkmark, or warning) based on the status.
    const getStatusIcon = (status: SyncStep['status']) => {
        switch (status) {
            case 'syncing': return <Spin size="small" />;
            case 'success': return <CheckCircle size={22} weight="fill" className="sync-icon sync-icon--success" />;
            case 'error': return <Warning size={22} weight="fill" className="sync-icon sync-icon--error" />;
            default: return <Circle size={22} weight="regular" className="sync-icon sync-icon--pending" />;
        }
    };

    // Show a nice icon like a sun for Solar or a battery for Battery.
    const getProductIcon = (type: string) => {
        const t = type.toLowerCase();
        if (t.includes('solar')) return <Sun size={14} weight="bold" />;
        if (t.includes('battery')) return <BatteryCharging size={14} weight="bold" />;
        if (t.includes('roof')) return <HouseLine size={14} weight="bold" />;
        return <List size={14} weight="bold" />;
    };

    return (
        <DashboardModal
            open={open}
            onClose={!isComplete && !overallError ? (() => {}) : handleCloseWrapper}
            footer={null}
            title={`Syncing with ${partnerName} Partner`}
            width={440}
            centered
            zIndex={OBD_FINANCE_PARTNER_PROGRESS_Z}
            theme={theme}
            className={`obd-battery-modal obd-theme-${theme}`}
            rootClassName={`obd-battery-modal-root obd-theme-${theme}`}
            maskClosable={false}
            destroyOnClose
        >
            <div className="obd-battery-modal__inner">
                <div className="obd-battery-modal__fields">
                    {/* Memoized progress bar to avoid re-calculating on every render */}
                    {React.useMemo(() => {
                        const totalSteps = productStates.reduce((acc, ps) => acc + ps.steps.length, 0);
                        const doneSteps = productStates.reduce((acc, ps) => acc + ps.steps.filter(s => s.status === 'success').length, 0);
                        const pct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;
                        
                        return (
                            <div className="sync-modal-bar__wrapper">
                                <div className="sync-modal-bar__info">
                                    <span>Overall Progress</span>
                                    <span>{pct}%</span>
                                </div>
                                <div className="sync-modal-bar__bg">
                                    <div className="sync-modal-bar__fill" style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    }, [productStates])}

                    <div className="sync-modal-content__scrollable">
                        {productStates.map((productState, pIdx) => (
                            <div key={productState.info.productType} className="sync-modal-product__section">
                                <div className="sync-modal-product__badge">
                                    {getProductIcon(productState.info.productType)}
                                    <span>{productState.info.productType}</span>
                                </div>
                                
                                <div className="sync-modal-steps__container">
                                    {productState.steps.map((step, index) => (
                                        <div key={step.id} className={`sync-modal-step__item sync-modal-step--${step.status}`}>
                                            <div className="sync-modal-step__visual">
                                                <div className="sync-modal-step__icon-wrapper">
                                                    {getStatusIcon(step.status)}
                                                </div>
                                                {index !== productState.steps.length - 1 && (
                                                    <div className="sync-modal-step__line" />
                                                )}
                                            </div>
                                            <div className="sync-modal-step__text">
                                                <div className="sync-modal-step__label">{step.label}</div>
                                                {step.status === 'syncing' && <div className="sync-modal-step__status-text">Processing...</div>}
                                                {step.status === 'success' && <div className="sync-modal-step__status-text sync-modal-text--success">Complete</div>}
                                                {step.status === 'error' && <div className="sync-modal-step__status-text sync-modal-text--error">Failed</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {overallError && (
                    <div className="sync-modal-error__card">
                        <Warning size={20} weight="fill" />
                        <div className="sync-modal-error__content">
                            <strong>Sync Interrupted</strong>
                            <p>{overallError}</p>
                        </div>
                    </div>
                )}
            </div>
            {(isComplete || overallError) && (
                <div className="obd-sync-modal__footer progress-modal-footer">
                    <button 
                        type="button" 
                        className="obd-sync-modal__btn obd-sync-modal__btn--primary" 
                        onClick={handleCloseWrapper}
                    >
                        Close
                    </button>
                </div>
            )}
        </DashboardModal>
    );
};

export default FinancePartnerCreateLeadProgressModal;
